import React, { Children, type ComponentProps, type ForwardedRef, forwardRef, type ReactNode } from 'react';

import { FILTER_CONTENT_PADDING, FILTER_HEADING_PADDING } from '../constants';

import { VariantKey } from './motion';

import { StyledAccordionPanel, StyledContainer, StyledItem, StyledList } from './styles';

type FilterContentProps = ComponentProps<typeof StyledAccordionPanel> & {
    children: ReactNode;
    height?: string;
    direction?: 'row' | 'column';
    noBorder?: boolean;
    customPadding?: number;
    scrollBarColor?: string;
    containScroll?: boolean;
};

/**
 * Make sure to set an index on AccordionItem!
 * Otherwise, index from useAccordionItemContext will initial be -1 and the isExpanded will be incorrect.
 */
export const FilterContent = ({
    children,
    height,
    direction = 'column',
    scrollBarColor,
    customPadding,
    noBorder,
    thinContent,
    containScroll,
    ...others
}: FilterContentProps) => {
    const paddingBottom = customPadding != undefined ? customPadding : FILTER_HEADING_PADDING * 2;
    const isExpanded = true;
    const listMarkup = Children.map(children, (child) => {
        if (!child) return null;
        return <StyledItem>{child}</StyledItem>;
    });

    return (
        <StyledContainer
            $noBorder={noBorder}
            transition={{ duration: 0, delay: 0.3 }}
            animate={
                isExpanded
                    ? {
                          paddingBottom: `${paddingBottom}px`,
                      }
                    : {
                          paddingBottom: thinContent ? 0 : `${FILTER_CONTENT_PADDING}px`,
                      }
            }
        >
            <AccordionContent direction={direction} {...others}>
                <StyledList $direction={direction} $paddingTop={10}>
                    {listMarkup}
                </StyledList>
            </AccordionContent>
        </StyledContainer>
    );
};

const AccordionContent = forwardRef(
    ({ children, ...props }: FilterContentProps, forwardedRef: ForwardedRef<HTMLDivElement>) => {
        return (
            <StyledAccordionPanel
                ref={forwardedRef}
                key={props.isExpanded}
                initial={props.isExpanded ? VariantKey.open : VariantKey.closed}
                animate={props.isExpanded ? VariantKey.open : props.hidden ? VariantKey.hidden : VariantKey.closed}
                $scrollBarColor={props.scrollBarColor}
                $containScroll={props.containScroll}
                {...props}
                {...props.others}
            >
                {children}
            </StyledAccordionPanel>
        );
    },
);

AccordionContent.displayName = 'AccordionContent';
